import React, { useEffect, useState } from "react";

/**
 * Compact per-row live/stale/delayed label.
 *   Props: status ("live" | "stale" | "delayed"), ageSec (number|null)
 *   Renders: `Live · 2s ago` / `Stale · 18s ago` / `Delayed · 4m ago`
 *
 * Ticks itself every second so the age counter stays current without
 * requiring the parent to re-render on every tick.
 */
export default function RowFreshness({ status = "delayed", ageSec = null }) {
  // Force a re-render every second so ageSec displays as an incrementing counter
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const label = status === "live" ? "Live" : status === "stale" ? "Stale" : "Delayed";
  const color =
    status === "live" ? "var(--bull)" :
    status === "stale" ? "var(--bear)" :
    "#ffd166";

  const display = formatAge(ageSec);

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono tracking-wider uppercase"
      style={{ color }}
      data-testid={`row-freshness-${status}`}
      title={`${label} • updated ${display}`}
    >
      <span
        className="w-1 h-1 rounded-full"
        style={{
          background: color,
          boxShadow: status === "live" ? `0 0 6px ${color}` : "none",
          animation: status === "live" ? "pulse 1.6s infinite" : "none",
        }}
      />
      {label}
      <span className="text-white/40 normal-case tracking-normal">· updated {display}</span>
    </span>
  );
}

function formatAge(ageSec) {
  if (ageSec == null || Number.isNaN(ageSec)) return "just now";
  const s = Math.max(0, Math.round(ageSec));
  if (s < 1) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}
